import '../app/polyfills';

import 'react-hot-loader/patch';
import { AppContainer } from 'react-hot-loader';
import React from 'react';
import ReactDOM from 'react-dom';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import DirectionProvider, { DIRECTIONS } from 'react-with-direction/dist/DirectionProvider';
import rtlDetect from 'rtl-detect';
import { ImgProvider, RENDER_IMAGE_WITH_PLACEHOLDER_INITIALLY } from '@wix/communities-image-lib';

import { fetchAndSetSitePages } from '../app/actions/fetch-and-set-site-pages';
import { createRequest, initClientI18n } from '@wix/communities-blog-client-common';
import { setConfig, getConfig } from '../app/services/config';
import listenToSettingsChange from '../app/services/listen-to-settings-change';
import listenToEditModeChange from '../app/services/listen-to-edit-mode-change';
import { getInstance } from '../app/services/wix-sdk-utils';
import * as appLifecycleService from '../app/services/app-lifecycle-service';
import createWidgetNavigation from '../app/services/navigation-provider/blog-widget';
import configureStore from '../app/store/configureStore';
import rootReducer from './reducers';
import actionLoggerMiddleware, { shouldLogReduxActions } from '../app/middleware/action-logger-middleware';
import NavigationProvider from '../app/components/navigation-provider';
import PermissionsProvider from '../app/components/permissions-provider';
import DirectionProviderWrapper from '../app/components/direction-provider-wrapper';
import Root from './components/root';
import { debug } from '../app/services/debug';

setConfig(window.__CONFIG__);

const baseUrl = window.__BASE_PATH__ || '';
const apiBaseUrl = window.__API_BASE_URL__ || '';
const instance = getInstance();
const instanceId = getConfig().instanceId;
const locale = window.__LOCALE__;
const request = createRequest({ apiBaseUrl, instance, instanceId, locale });
const initialState = window.__INITIAL_STATE__;
const i18n = initClientI18n();

const middlewares = [
  thunk.withExtraArgument({
    request,
    baseUrl: baseUrl || `${window.location.protocol}//${window.location.host}`,
  }),
];

shouldLogReduxActions() && middlewares.push(actionLoggerMiddleware);

const store = configureStore(initialState || {}, middlewares, rootReducer);
store.dispatch(fetchAndSetSitePages());

listenToEditModeChange(store);
listenToSettingsChange(store);

const renderApp = (Application) => {
  appLifecycleService.appIsLoading();
  const renderFn = ReactDOM.hydrate || ReactDOM.render;
  renderFn(
    <AppContainer>
      <Provider store={store}>
        <NavigationProvider navigation={createWidgetNavigation(store)}>
          <PermissionsProvider store={store}>
            <I18nextProvider i18n={i18n}>
              <DirectionProviderWrapper>
                <DirectionProvider direction={rtlDetect.isRtlLang(i18n.language) ? DIRECTIONS.RTL : DIRECTIONS.LTR}>
                  <ImgProvider renderMode={RENDER_IMAGE_WITH_PLACEHOLDER_INITIALLY}>
                    <Application />
                  </ImgProvider>
                </DirectionProvider>
              </DirectionProviderWrapper>
            </I18nextProvider>
          </PermissionsProvider>
        </NavigationProvider>
      </Provider>
    </AppContainer>,
    document.getElementById('root'),
  );
};

renderApp(Root);

if (module.hot) {
  module.hot.accept('./components/root', () => {
    debug.log('HOT RELOAD START');
    // While using 'separateCss: true', external css files must be manually reloaded
    document.querySelectorAll('link[href][rel=stylesheet]').forEach((link) => {
      const nextStyleHref = link.href.replace(/(\?\d+)?$/, `?${Date.now()}`);
      link.href = nextStyleHref;
    });

    renderApp(require('./components/root').default);
    debug.log('HOT RELOAD END');
  });
}
